import React, { useRef, useEffect, useState } from "react";
import clsx from "clsx";

import type { ExcalidrawElement, NonDeleted } from "@excalidraw/element/types";

import { LibraryUnit } from "../LibraryUnit";

import { canUsePremiumItem } from "../../data/premiumAuth";

import type { SvgCache } from "../../hooks/useLibraryItemSvg";
import type { PremiumLibraryItem } from "../../data/premiumLibraryData";

interface PremiumLibraryUnitProps {
  elements?: readonly NonDeleted<ExcalidrawElement>[];
  isPending?: boolean;
  onClick: (id: PremiumLibraryItem["id"] | null) => void;
  svgCache: SvgCache;
  id: PremiumLibraryItem["id"] | null;
  selected: boolean;
  onToggle: (id: PremiumLibraryItem["id"], event: React.MouseEvent) => void;
  onDrag: (id: PremiumLibraryItem["id"], event: React.DragEvent) => void;
  item: PremiumLibraryItem;
  onPremiumPrompt?: (item: PremiumLibraryItem) => void;
}

export const PremiumLibraryUnit: React.FC<PremiumLibraryUnitProps> = ({
  elements,
  isPending,
  onClick,
  svgCache,
  id,
  selected,
  onToggle,
  onDrag,
  item,
  onPremiumPrompt,
}) => {
  const unitRef = useRef<HTMLDivElement>(null);
  const canUse = canUsePremiumItem(item.tier);

  const handleClick = (itemId: PremiumLibraryItem["id"] | null) => {
    if (item.tier === "premium" && !canUse) {
      // Show premium prompt instead of inserting
      onPremiumPrompt?.(item);
      return;
    }
    onClick(itemId);
  };

  const handleToggle = (
    itemId: PremiumLibraryItem["id"],
    event: React.MouseEvent,
  ) => {
    if (item.tier === "premium" && !canUse) {
      event.preventDefault();
      onPremiumPrompt?.(item);
      return;
    }
    onToggle(itemId, event);
  };

  const handleDrag = (
    itemId: PremiumLibraryItem["id"],
    event: React.DragEvent,
  ) => {
    if (item.tier === "premium" && !canUse) {
      event.preventDefault();
      return;
    }
    onDrag(itemId, event);
  };

  return (
    <div
      ref={unitRef}
      className={clsx("premium-library-unit", {
        "premium-library-unit--premium": item.tier === "premium",
      })}
      title={item.name || "Library Item"}
    >
      <LibraryUnit
        elements={elements}
        isPending={isPending}
        onClick={handleClick}
        svgCache={svgCache}
        id={id}
        selected={selected}
        onToggle={handleToggle}
        onDrag={handleDrag}
      />

      {item.tier === "premium" && (
        <div className="premium-library-unit__badge">
          <span className="premium-library-unit__badge-text">Pro</span>
        </div>
      )}
    </div>
  );
};
